import React, { useState } from 'react';
import {
  Search,
  Filter,
  RefreshCw,
  AlertCircle,
  Store,
  CheckCircle,
  Clock,
  Eye,
  ShieldCheck,
  ShieldX,
  Share2,
} from 'lucide-react';

import {
  useSuperAdminVendors,
  useUpdateVendorVerification,
  useUpdateVendorCommission,
  useUpdateVendorReferral,
} from '@/features/super-admin/hooks/useSuperAdminVendors';
import VendorDetailModal from '@/features/super-admin/components/vendors/VendorDetailModal';
import { formatJalaliDate } from '@/lib/format';
import { formatNumber, formatPercent, persianDigits } from '@/features/reports/lib/format';
import { persianDisplayText } from '@/lib/persianUi';

const VERIFICATION_STATUSES = [
  { value: 'ALL', label: 'همه وضعیت‌ها' },
  { value: 'pending', label: 'در انتظار تایید' },
  { value: 'verified', label: 'تایید شده' },
];

export default function VendorApprovalTable() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, isFetching, error, refetch } = useSuperAdminVendors({
    page,
    size: 10,
    search: appliedSearch || undefined,
    verification_status: statusFilter !== 'ALL' ? statusFilter : undefined,
  });

  const vendors = data?.items ?? [];
  const totalPages = data?.pages ?? 0;

  const verifyMutation = useUpdateVendorVerification();
  const commissionMutation = useUpdateVendorCommission();
  const referralMutation = useUpdateVendorReferral();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmed = search.trim();

    if (trimmed === appliedSearch && page === 1) {
      refetch();
    } else {
      setAppliedSearch(trimmed);
      setPage(1);
    }
  };

  const handleVerifyToggle = (vendorId, isVerified) => {
    verifyMutation.mutate(
      { vendorId, isVerified },
      {
        onSuccess: (updatedVendor) => {
          if (selectedVendor?.id === vendorId) {
            setSelectedVendor(updatedVendor);
          }
        },
        onError: (err) => {
          console.error(err);
          alert('خطا در تغییر وضعیت تایید فروشنده');
        },
      }
    );
  };

  const handleReferralToggle = (vendorId, referralEnabled) => {
    referralMutation.mutate(
      { vendorId, referralEnabled },
      {
        onSuccess: (updatedVendor) => {
          if (selectedVendor?.id === vendorId) {
            setSelectedVendor(updatedVendor);
          }
        },
        onError: (err) => {
          console.error(err);
          alert('خطا در تغییر وضعیت برنامه معرفی');
        },
      }
    );
  };

  const handleCommissionUpdate = async (vendorId, commissionRate) => {
    const updatedVendor = await commissionMutation.mutateAsync({ vendorId, commissionRate });
    setSelectedVendor(updatedVendor);
    return updatedVendor;
  };

  const openDetailModal = (vendor) => {
    setSelectedVendor(vendor);
    setIsModalOpen(true);
  };

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">مدیریت و تایید فروشندگان</h1>
          <p className="admin-page-subtitle">بررسی و تایید صلاحیت فروشندگان سیستم</p>
        </div>

        <button onClick={() => refetch()} className="admin-refresh-btn" disabled={isFetching}>
          <RefreshCw size={16} className={isFetching ? 'spinning' : ''} />
          <span>بروزرسانی</span>
        </button>
      </div>

      <div className="card admin-toolbar">
        <form onSubmit={handleSearchSubmit} className="admin-search">
          <input
            type="text"
            placeholder="جستجو با نام فروشگاه، مالک یا شماره تماس..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search size={16} />
        </form>

        <div className="admin-filter">
          <Filter size={16} />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {VERIFICATION_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card admin-table-card">
        {error && (
          <div className="admin-table-error">
            <AlertCircle size={16} />
            <span>خطا در دریافت لیست فروشندگان</span>
          </div>
        )}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>فروشگاه</th>
                <th>مالک</th>
                <th>شماره تماس</th>
                <th>تاریخ ثبت‌نام</th>
                <th>نرخ کمیسیون</th>
                <th>وضعیت احراز</th>
                <th>برنامه معرفی</th>
                <th style={{ textAlign: 'center' }}>عملیات</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="admin-table-empty">
                    در حال دریافت اطلاعات...
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr>
                  <td colSpan="8" className="admin-table-empty">
                    فروشنده‌ای یافت نشد.
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => {
                  const isVerifyUpdating =
                    verifyMutation.isPending && verifyMutation.variables?.vendorId === vendor.id;
                  const isReferralUpdating =
                    referralMutation.isPending && referralMutation.variables?.vendorId === vendor.id;

                  return (
                    <tr key={vendor.id}>
                      <td>
                        <div className="vendor-cell">
                          <div className="vendor-cell-icon">
                            <Store size={16} />
                          </div>
                          <div>
                            <p className="admin-cell-primary">
                              {persianDisplayText(vendor.vendor_shop_name_fa, 'فروشگاه بدون نام')}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td>
                        <p className="admin-cell-primary">
                          {vendor.first_name
                            ? `${persianDisplayText(vendor.first_name, 'بدون نام')} ${persianDisplayText(vendor.last_name, '')}`
                            : 'بدون نام'}
                        </p>
                        <p className="admin-cell-secondary">{vendor.email || '—'}</p>
                      </td>

                      <td className="admin-cell-secondary" style={{ fontFamily: 'monospace' }}>
                        {persianDigits(vendor.vendor_local_phone || vendor.mobile_phone) || '—'}
                      </td>

                      <td className="admin-cell-secondary">
                        {vendor.created_at ? formatJalaliDate(vendor.created_at) : '—'}
                      </td>

                      <td className="admin-cell-primary">{vendor.commission_rate == null ? '—' : formatPercent(vendor.commission_rate)}</td>

                      <td>
                        {vendor.is_verified ? (
                          <span className="status-badge-sm status-verified-badge">
                            <CheckCircle size={12} style={{ marginLeft: 4 }} />
                            تایید شده
                          </span>
                        ) : (
                          <span className="status-badge-sm status-unverified-badge">
                            <Clock size={12} style={{ marginLeft: 4 }} />
                            در انتظار تایید
                          </span>
                        )}
                      </td>

                      <td>
                        {vendor.referral_enabled ? (
                          <span className="status-badge-sm status-verified-badge">
                            <Share2 size={12} style={{ marginLeft: 4 }} />
                            فعال
                          </span>
                        ) : (
                          <span className="status-badge-sm status-unverified-badge">
                            غیرفعال
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="row-actions">
                          <button
                            onClick={() => openDetailModal(vendor)}
                            className="icon-btn-sm"
                            title="مشاهده جزییات"
                          >
                            <Eye size={16} />
                          </button>

                          {vendor.is_verified ? (
                            <button
                              disabled={isVerifyUpdating}
                              onClick={() => handleVerifyToggle(vendor.id, false)}
                              className="btn-sm btn-sm-danger"
                            >
                              <ShieldX size={14} />
                              <span>لغو تایید</span>
                            </button>
                          ) : (
                            <button
                              disabled={isVerifyUpdating}
                              onClick={() => handleVerifyToggle(vendor.id, true)}
                              className="btn-sm btn-sm-success"
                            >
                              <ShieldCheck size={14} />
                              <span>تایید صلاحیت</span>
                            </button>
                          )}

                          <button
                            disabled={isReferralUpdating}
                            onClick={() => handleReferralToggle(vendor.id, !vendor.referral_enabled)}
                            className={`btn-sm ${vendor.referral_enabled ? 'btn-sm-danger' : 'btn-sm-success'}`}
                          >
                            <Share2 size={14} />
                            <span>
                              {vendor.referral_enabled ? 'غیرفعال‌سازی معرفی' : 'فعال‌سازی معرفی'}
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <span>
              صفحه {formatNumber(page)} از {formatNumber(totalPages)}
            </span>

            <div className="admin-pagination-btns">
              <button disabled={page === 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
                قبلی
              </button>
              <button disabled={page === totalPages} onClick={() => setPage((p) => Math.min(p + 1, totalPages))}>
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>

      <VendorDetailModal
        key={`${selectedVendor?.id ?? 'vendor'}-${selectedVendor?.commission_rate ?? ''}`}
        isOpen={isModalOpen}
        vendor={selectedVendor}
        onClose={() => setIsModalOpen(false)}
        onVerifyToggle={handleVerifyToggle}
        onCommissionUpdate={handleCommissionUpdate}
        commissionUpdating={
          commissionMutation.isPending && commissionMutation.variables?.vendorId === selectedVendor?.id
        }
      />
    </div>
  );
}